<!-- eslint-disable vue/singleline-html-element-content-newline vue/max-attributes-per-line -->
<template>
  <div class="expenses-table-wrapper">
    <v-skeleton-loader
      v-if="isMounting"
      type="table"
    />

    <v-data-table
      v-else
      :loading="isLoading"
      :headers="headers"
      :items="items"
      no-data-text="データがありません"
      loading-text="読込中..."
      :header-props="{ sortByText: 'ソート' }"
      :footer-props="{ itemsPerPageText: '行/ページ:' }"
      sort-by="meta.date"
      :sort-desc="true"
      :items-per-page="-1"
      class="expenses-table"
    >
      <template #top>
        <v-toolbar flat color="transparent">
          <ExpenseFilterMenu
            :filters="filters"
            :unique-primary-labels="uniquePrimaryLabels"
            :filtered-secondary-labels="filteredSecondaryLabels"
            :filtered-tertiary-labels="filteredTertiaryLabels"
            :filtered-quaternary-labels="filteredQuaternaryLabels"
            :show-tertiary-columns="showTertiaryColumns"
          />
          <v-spacer />
          <v-btn
            icon
            depressed
            :ripple="false"
            :loading="isLoading"
            class="ml-2 mr-1"
            @click="reloadExpenses"
          >
            <v-icon>{{ icons.mdiReload }}</v-icon>
          </v-btn>
          <v-divider
            vertical
            inset
            class="mx-2"
          />
          <v-menu
            bottom
            left
          >
            <template #activator="{ on: menuOn, attrs: menuAttrs }">
              <v-btn
                icon
                v-bind="menuAttrs"
                v-on="menuOn"
              >
                <v-icon>{{ icons.mdiDotsVertical }}</v-icon>
              </v-btn>
            </template>

            <template #default="menu">
              <v-list
                :disabled="isLoading"
                dense
              >
                <v-subheader>操作</v-subheader>
                <v-list-item
                  @click="showTertiaryColumns = !showTertiaryColumns; menu.value = false"
                >
                  <v-list-item-title>
                    {{ showTertiaryColumns ? '詳細分類を非表示' : '詳細分類を表示' }}
                  </v-list-item-title>
                </v-list-item>
                <DataTableCopyListItemBtn
                  :disabled="isLoading"
                  :headers="headers"
                  :items="copyItems"
                />
              </v-list>
            </template>
          </v-menu>
        </v-toolbar>
      </template>

      <template #[`item.amount`]="{ item }">
        ¥{{ item.amount.toLocaleString('ja-JP') }}
      </template>

      <template #[`item.taxRate`]="{ item }">
        {{ getTaxRateText(item.taxRate) }}
      </template>

      <template #[`item.taxAmount`]="{ item }">
        {{ getTaxAmountDisplay(item) }}
      </template>

      <template #[`item.isNonCash`]="{ item }">
        <v-checkbox
          :value="item.isNonCash === '○'"
          hide-details
          dense
          readonly
        />
      </template>

      <template #foot>
        <tfoot>
          <tr>
            <td :colspan="headers.length - 4" class="text-right pr-4">金額合計：</td>
            <td>
              ¥{{ totalAmount.toLocaleString('ja-JP') }}
            </td>
            <template v-if="totalTaxAmount > 0">
              <td class="text-right pr-4"> 消費税合計：</td>
              <td>
                ¥{{ totalTaxAmount.toLocaleString('ja-JP') }}
              </td>
            </template>
            <td></td>
          </tr>
        </tfoot>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import {
  ref,
  toRefs,
  computed,
  onMounted,
} from '@vue/composition-api'
import {
  mdiReload,
  mdiTrashCanOutline,
  mdiDotsVertical,
} from '@mdi/js'
import { startOfMonth, format } from 'date-fns'

import ExpenseApi from '@/api/v2/report/Expense'
import useCompInit from '@/views/composable/useCompInit'
import useUserPreferences from '@/views/v2-temp/common/composables/useUserPreferences'
import useExpenseFilters from '../composables/useExpenseFilters'
import useTaxCalculation from '../composables/useTaxCalculation'
import useExpenseTable from '@/views/v2-temp/eod-closing/composables/useExpenseTable'
import DataTableCopyListItemBtn from '@/views/v2-temp/common/components/DataTableCopyListItemBtn.vue'
import ExpenseFilterMenu from './ExpenseFilterMenu.vue'

export default {
  components: {
    DataTableCopyListItemBtn,
    ExpenseFilterMenu,
  },
  props: {
    date: {
      type: String,
      required: true,
      default: () => {
        return format(startOfMonth(new Date()), 'yyyy-MM-dd')
      },
    },
    interval: {
      type: Number,
      default: 2,
    },
    endDate: {
      default: null,
      validator: val => (val === null || typeof val === 'string'),
    },
  },
  setup(props) {
    const { date, interval, endDate } = toRefs(props)
    const { isLoading, initWith } = useCompInit()
    const { preferences } = useUserPreferences()

    const isMounting = ref(true)
    const expenses = ref([])

    const showTertiaryColumns = computed({
      get: () => preferences.value['eod_closing:expenses'].showTertiaryColumns,
      set: val => {
        preferences.value['eod_closing:expenses'].showTertiaryColumns = val
      },
    })

    const {
      filters,
      uniquePrimaryLabels,
      filteredSecondaryLabels,
      filteredTertiaryLabels,
      filteredQuaternaryLabels,
      filteredItems,
    } = useExpenseFilters(expenses)

    const {
      taxRateOptions,
      getTaxRateText,
      getTaxAmountDisplay,
      getTaxAmountValue,
      calculateTaxAmount,
    } = useTaxCalculation()

    const { headers } = useExpenseTable({
      showTertiaryColumns, // computed をそのまま渡す（reactive に更新される）
      includeActions: false, // actions列は不要
      includeDate: true, // 日付列を含む
      useColumnWidths: false, // 列幅設定は不要（必要に応じて true に変更可能）
      useLabelIds: false, // ラベル名を使用（expensePrimaryLabelName など）
    })

    const items = computed(() => {
      return filteredItems.value.map(item => {
        return {
          expenseDate: item.meta.date || '',
          expensePrimaryLabelName: item.meta.expensePrimaryLabelName || '',
          expenseSecondaryLabelName: item.meta.expenseSecondaryLabelName || '',
          expenseTertiaryLabelName: item.meta.expenseTertiaryLabelName || '',
          expenseQuaternaryLabelName: item.meta.expenseQuaternaryLabelName || '',
          amount: item.amount || 0,
          taxRate: item.taxRate || 'blank',
          isNonCash: item.meta.isNonCash ? '○' : '',
        }
      })
    })

    // コピー用のデータ構造
    const copyItems = computed(() => {
      return items.value.map(item => {
        const taxRateText = getTaxRateText(item.taxRate)
        const taxAmount = calculateTaxAmount(item.amount, item.taxRate)

        return {
          expenseDate: item.expenseDate || '',
          expensePrimaryLabelName: item.expensePrimaryLabelName || '',
          expenseSecondaryLabelName: item.expenseSecondaryLabelName || '',
          expenseTertiaryLabelName: item.expenseTertiaryLabelName || '',
          expenseQuaternaryLabelName: item.expenseQuaternaryLabelName || '',
          amount: item.amount,
          taxRate: taxRateText,
          taxAmount: taxAmount !== null ? taxAmount : '',
          isNonCash: item.isNonCash,
        }
      })
    })

    const totalAmount = computed(() => {
      return filteredItems.value.reduce((sum, item) => {
        return sum + (item.amount || 0)
      }, 0)
    })

    const totalTaxAmount = computed(() => {
      return filteredItems.value.reduce((sum, item) => {
        return sum + (getTaxAmountValue(item) || 0)
      }, 0)
    })

    /**
     * API: 諸経費一覧取得
     */
    const getExpenses = async () => {
      isLoading.value = true
      try {
        const res = await ExpenseApi.getExpenses(date.value, interval.value, endDate.value)
        if (res.data.status === 'success') {
          expenses.value = res.data.data.data
        }
      } finally {
        isLoading.value = false
      }
    }

    const reloadExpenses = async () => {
      await Promise.all([
        getExpenses(),
      ])
    }

    onMounted(async () => {
      await initWith([
        getExpenses(),
      ])
      isMounting.value = false
    })

    return {
      // refs
      isMounting,
      showTertiaryColumns,
      filters,

      // computed
      items,
      copyItems,
      headers,
      expenses,
      totalAmount,
      totalTaxAmount,
      uniquePrimaryLabels,
      filteredSecondaryLabels,
      filteredTertiaryLabels,
      filteredQuaternaryLabels,

      // tax calculation
      getTaxRateText,
      getTaxAmountDisplay,
      getTaxAmountValue,

      // methods
      reloadExpenses,

      // from useCompInit
      isLoading,

      // icons
      icons: {
        mdiReload,
        mdiTrashCanOutline,
        mdiDotsVertical,
      },
    }
  },
}
</script>

<style lang="scss">
@import '~@core/preset/preset/mixins.scss';

@include theme--child(expenses-table) using ($material) {
  th.fixed-cell,
  td.fixed-cell {
    position: sticky !important;
    position: -webkit-sticky !important;
    left: 0;
    z-index: 3;
    background: map-deep-get($material, 'app-bar');
  }

  th.fixed-cell {
    z-index: 4;
  }

  .v-progress-linear {
    z-index: 5;
  }
}

.expenses-table-wrapper {
  overflow-x: auto;
}

.expenses-table {
  white-space: nowrap;
}
</style>
